package one.healthium.life.presentation.feature.today

import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.statusBarsPadding
import androidx.compose.foundation.relocation.BringIntoViewRequester
import androidx.compose.foundation.relocation.bringIntoViewRequester
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.LocalContentColor
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.CompositionLocalProvider
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.remember
import androidx.compose.ui.Modifier
import androidx.compose.ui.platform.testTag
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.em
import loco.LIFE_TODAY_DAILY_TASKS_SECTION
import loco.TODAY_LOG_CONTROLS_SECTION
import one.healthium.healthKit.ui.HealthKitConnectBanner
import one.healthium.healthKit.ui.HealthKitConnectBannerContract
import one.healthium.health_advice.ui.message.HealthAdviceMessage
import one.healthium.life.presentation.feature.today.components.TodayCaloriesCounter
import one.healthium.life.presentation.feature.today.components.TodayDailyControls
import one.healthium.life.presentation.feature.today.components.TodayDailyTasks
import one.healthium.meal_plans.domain.MealPlanInfoModel
import one.healthium.rateus.data.ShowAppRateForStart
import one.healthium.tasks.domain.DailyTaskModel
import one.healthium.today.ui.TodayUiState
import one.healthium.today.ui.TodayViewModel
import one.healthium.today.ui.components.TodayUserProfile
import one.healthium.today.ui.models.DailyTaskUiModel
import one.healthium.ui.common.message.AlertUiMessageHandler
import one.healthium.ui.compose.components.AppScaffold
import one.healthium.ui.compose.platform.PlatformColorsEffect
import one.healthium.ui.compose.theme.AppTheme
import one.healthium.ui.compose.theme.green_11
import org.jetbrains.compose.resources.stringResource
import org.koin.compose.viewmodel.koinViewModel

interface TodayEventDelegate {
    fun onLogWeightClicked()
    fun onLogWaterClicked()
    fun onMealPlanClicked(id: String)
    fun onDailyTaskClicked(dailyTask: DailyTaskModel)
    fun onLogFoodClicked()
    fun onLogSymptomsClicked()
}

@Composable
fun TodayScreen(eventDelegate: TodayEventDelegate) {
    val viewModel = koinViewModel<TodayViewModel>()
    val state by viewModel.state().collectAsState()

    PlatformColorsEffect(lightStatusBar = false)

    ShowAppRateForStart()

    TodayScreenContent(
        state = state,
        onMealPlanClicked = { eventDelegate.onMealPlanClicked(it.id) },
        onLogWeightClicked = eventDelegate::onLogWeightClicked,
        onLogWaterClicked = eventDelegate::onLogWaterClicked,
        onDailyTaskClicked = { eventDelegate.onDailyTaskClicked(it.model) },
        onLogFoodClicked = eventDelegate::onLogFoodClicked,
        onLogSymptomsClicked = eventDelegate::onLogSymptomsClicked,
        healthKitContract = viewModel,
    )

    AlertUiMessageHandler(viewModel.uiMessages)

}

@Composable
internal fun TodayScreenContent(
    state: TodayUiState,
    onLogWeightClicked: () -> Unit,
    onLogWaterClicked: () -> Unit,
    onMealPlanClicked: (MealPlanInfoModel) -> Unit,
    onDailyTaskClicked: (DailyTaskUiModel) -> Unit,
    onLogFoodClicked: () -> Unit,
    onLogSymptomsClicked: () -> Unit,
    healthKitContract: HealthKitConnectBannerContract,
) {
    val bringIntoViewRequester = remember { BringIntoViewRequester() }

    AppScaffold(Modifier.testTag("today_screen")) {
        Column(
            modifier = Modifier
                .verticalScroll(rememberScrollState())
                .testTag("today_content"),
        ) {
            Box(
                Modifier
                    .bringIntoViewRequester(bringIntoViewRequester)
                    .statusBarsPadding()
                    .size(12.dp)
            )
            val userProfile = state.userProfile
            if (userProfile != null) {
                CompositionLocalProvider(LocalContentColor provides green_11) {
                    TodayUserProfile(
                        modifier = Modifier
                            .fillMaxWidth()
                            .padding(horizontal = 24.dp),
                        userProfile = userProfile,
                    )
                }
            }
            Spacer(Modifier.height(4.dp))
            HealthAdviceMessage(
                bringIntoViewRequester = bringIntoViewRequester,
                onLogSymptomsClicked = onLogSymptomsClicked,
                textAlign = TextAlign.Start,
                style = AppTheme.textStyles.secondary_17_400,
                color = green_11,
                lineHeight = 1.5.em,
            )
            Spacer(Modifier.height(8.dp))
            TodayCaloriesCounter(
                modifier = Modifier.padding(horizontal = 24.dp),
                model = state.todayCaloriesCounter,
                onLogFoodClicked = onLogFoodClicked,
            )
            if (state.shouldShowHKBanner) {
                Spacer(Modifier.height(24.dp))
                HealthKitConnectBanner(
                    modifier = Modifier.padding(horizontal = 12.dp),
                    contract = healthKitContract,
                )
            }
            val dailyTasks = state.dailyTasksUncompleted
            if (dailyTasks != null) {
                Section(stringResource(loco.Res.string.LIFE_TODAY_DAILY_TASKS_SECTION))
                TodayDailyTasks(
                    dailyTasks = dailyTasks,
                    onClick = onDailyTaskClicked,
                )
            }
            Section(stringResource(loco.Res.string.TODAY_LOG_CONTROLS_SECTION))
            TodayDailyControls(
                modifier = Modifier.padding(horizontal = 24.dp),
                state = state,
                onLogWeightClicked = onLogWeightClicked,
                onLogWaterClicked = onLogWaterClicked,
                onMealPlanClicked = onMealPlanClicked,
                onLogSymptomsClicked = onLogSymptomsClicked,
            )
            Spacer(Modifier.height(100.dp))
        }
    }
}

@Composable
private fun Section(text: String) {
    Text(
        modifier = Modifier
            .fillMaxWidth()
            .padding(top = 24.dp, bottom = 12.dp, start = 24.dp, end = 24.dp),
        text = text,
        style = AppTheme.textStyles.secondary_16_700,
        color = AppTheme.colors.text100,
        maxLines = 1,
    )
}